<template>
  <div>
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h3 mr-r15">가상화 모델링 목록</div>
        <div class="setting-addel mr-r4">
          <el-button size="mini" @click="clickAddButton"><i class="fas fa-plus"></i></el-button>
          <el-button size="mini" :disabled="checkedRows.length === 0" @click="clickDeleteButton(checkedRows)"><i class="fas fa-minus"></i></el-button>
        </div>
        <el-button :disabled="checkedRows.length === 0" @click="clickModelingButton(checkedRows)">모델링 수행</el-button>
      </div>
      <div class="vertical-center-box">
        <el-button class="mr-r4" type="primary" @click="clickSaveButton" :disabled="!changeTableDataFlag">저장</el-button>
        <el-button class="mr-r4" @click="clickCancelButton" :disabled="!changeTableDataFlag">취소</el-button>
        <w-header-search-button size="mini" @click="showSearchTool = !showSearchTool"></w-header-search-button>
      </div>
    </el-row>
    <el-row>
      <el-table-wrapper :data="tableData" :columns="tableColumns" :column-default="columnDefault" row-class-name="c-dblclick" ref="virtualCenterTable" :pagination="true" :show-search-tool="showSearchTool"
       :show-custom-header="true" @condition-change="changeTableCondition" @selection-change="changeCheckedRow" @row-dblclick="onRowDblClick" remote :total="total">
         <template slot-scope="scope" slot="active-flag-slot">
          <el-switch v-model="scope.row.activeFlag" active-value="Y" inactive-value="N" @change="checkChangeTableDataFlag()"></el-switch>
        </template>
        <template slot-scope="scope" slot="modeling-slot">
          <i class="fas fa-play-circle text-14 c-pointer" @click="clickModelingButton([scope.row])"></i>
        </template>
      </el-table-wrapper>
    </el-row>

    <!-- 가상화 모델링 추가 팝업 -->
    <w-modeling-setting-popup :popup-params="popupParams" ref="modelingSettingPopup" @re-load="loadData"></w-modeling-setting-popup>

  </div>
</template>

<script>
import { differenceWith, cloneDeep, isNil, isEqual, map } from 'lodash';
import DeviceRegistrationVmsAPI from '@/api/settings/common/device-registration-vms';
import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { isTrue } from '@/utils/boolean';
import MsgUtil from '@/utils/message';
import { columnDefault } from '@/constants/table';
import WModelingSettingPopup from './popup/ModelingSettingPopup';

const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);

export default {
  name: 'WModeling',
  components: {
    WModelingSettingPopup,
  },
  props: {
    rscEventRuleList: Array,
    eventAlarmRuleList: Array,
  },
  data() {
    return {
      columnDefault,
      popupParams: {},
      showSearchTool: false,
      total: 0,
      tableData: [],
      tableOriginData: [],
      changeTableDataFlag: false,
      checkedRows: [],
      tableColumns: [
        {
          // align: 'center',
          type: 'selection',
        },
        {
          prop: 'activeFlag',
          label: '활성여부',
          // align: 'center',
          scopedSlot: 'active-flag-slot',
          width: 80,
          remoteProp: 'active_flag',
          filters: [
            {
              text: '활성',
              value: 'Y',
            },
            {
              text: '비활성',
              value: 'N',
            },
          ],
        },
        {
          prop: 'virtualcenterGroupName',
          label: '가상화센터명',
          width: 150,
          remoteProp: 'virtualcenter_group_name',
          searchable: true,
        },
        {
          prop: 'hypervisorType',
          label: '하이퍼바이저 종류',
          width: 150,
          remoteProp: 'vvg.hypervisor_type',
          searchable: true,
        },
        {
          prop: 'virtualcenterUrl',
          label: '접속정보',
          remoteProp: 'virtualcenter_url',
          formatter: this.formatVirtualcenterUrl,
          searchable: true,
        },
        // {
        //   prop: 'virtualcenterManagerId',
        //   label: '로그인ID',
        //   width: 100,
        //   remoteProp: 'virtualcenter_manager_id',
        //   searchable: true,
        // },
        {
          prop: 'schedulePeriod',
          label: '수행 주기',
          width: 80,
          remoteProp: 'schedule_period',
          formatter: this.formatSchedulePeriod,
          filters: [
            {
              text: '한번',
              value: 'N',
            },
            {
              text: '매일',
              value: 'D',
            },
            {
              text: '매주',
              value: 'W',
            },
            {
              text: '월간',
              value: 'M',
            },
          ],
        },
        {
          prop: 'scheduleContent',
          label: '스케줄 정보',
          formatter: defaultFormatter,
          remoteProp: 'schedule_content',
          searchable: true,
        },
        {
          prop: 'currentAutodiscoveryDatetime',
          label: '최근 수행 일시',
          width: 130,
          align: 'center',
          formatter: this.getDateFormatter,
          remoteProp: 'vas.check_datetime',
          searchable: true,
          searchType: 'date',
        },
        {
          prop: 'apiReply',
          label: '수행결과',
          width: 80,
          align: 'center',
          formatter: this.formatModelingResult,
        },
        {
          label: '모델링 수행',
          width: 80,
          align: 'center',
          scopedSlot: 'modeling-slot',
        },
      ],
    };
  },
  methods: {
    /**
     * 가상화 센터 리스트 로드
     * @author jeee12
     */
    loadData() {
      const tableParams = this.$refs.virtualCenterTable.getParameter();
      const params = {
        ...tableParams,
      };
      DeviceRegistrationVmsAPI.getVirtualModelingList(params).then((response) => {
        this.total = response.data.total;
        this.tableData = cloneDeep(response.data.data);
        this.tableOriginData = cloneDeep(response.data.data);
        this.$nextTick(() => {
          this.checkChangeTableDataFlag();
        });
      });
    },
    /**
     * 저장버튼 클릭
     * @author jeee12
     */
    clickSaveButton() {
      DeviceRegistrationVmsAPI.updateVirtualModelingList(differenceWith(this.tableData, this.tableOriginData, isEqual)).then((response) => {
        if (response.data.success) {
          this.loadData();
        }
      });
    },
    /**
     * 취소버튼 클릭
     * @author jeee12
     */
    clickCancelButton() {
      this.loadData();
    },
    /**
     * 테이블 값이 수정됐는지 체크
     * @author jeee12
     */
    checkChangeTableDataFlag() {
      this.changeTableDataFlag = !isEqual(this.tableData, this.tableOriginData);
    },
    /**
     * 테이블 selection change
     * @author jeee12
     */
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    /**
     * 테이블 row 더블클릭, 가상화 모델링 수정팝업 open
     * @author jeee12
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    onRowDblClick(row) {
      this.popupParams = {
        popupType: 'UPDATE',
        popupTitle: '가상화 모델링 수정',
        selectedRow: cloneDeep(row),
        scheduleData: cloneDeep(row),
      };
      this.$refs.modelingSettingPopup.open();
    },
    /**
     * 가상화 센터 추가
     * @author jeee12
     * @return {[type]} [description]
     */
    clickAddButton() {
      this.popupParams = {
        popupType: 'ADD',
        popupTitle: '가상화 모델링 추가',
      };
      this.$refs.modelingSettingPopup.open();
    },
    /**
     * 선택된 가상화 센터 삭제
     * @author jeee12
     * @return {[type]} [description]
     */
    clickDeleteButton(selectedRows) {
      const messageOptions = {
        dangerouslyUseHTMLString: true,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        closeOnHashChange: false,
        showClose: false,
        ...MsgUtil.defaultConfirmButton,
      };

      this.$confirm('가상화센터를 삭제하면 연관된 하위 관리대상이 모두 삭제됩니다.<br />삭제하시겠습니까?', '확인', messageOptions).then(() => {
        DeviceRegistrationVmsAPI.deleteVirtualModelingList(selectedRows).then((response) => {
          if (response.data.success) {
            this.loadData();
          }
        }).catch(() => {});
      }).catch(() => {});
    },
    /**
     * 모델링 수행
     * @author jeee12
     * @return {[type]} [description]
     */
    clickModelingButton(selectedRows) {
      const virtualcenterGroupIds = map(selectedRows, (e) => e.virtualcenterGroupId);
      this.$confirm(...MsgUtil.getCustomConfirmData('모델링을 수행하시겠습니까?')).then(() => {
        DeviceRegistrationVmsAPI.executeAutodiscovery(virtualcenterGroupIds).then((response) => {
          if (response.data.success) {
            const modelingDatas = response.data.data;
            modelingDatas.forEach((e, index) => {
              if (!e.autodiscoveryStatusFlag || !isNil(e.apiReply)) {
                this.$notify({
                  title: '모델링 수행',
                  message: `${e.virtualcenterName}의 모델링을 시작하였습니다.`,
                  offset: index * 70,
                  type: 'success',
                });
              } else {
                this.$notify.error({
                  title: '모델링 수행',
                  message: `${e.virtualcenterName}의 모델링이 이미 수행 중입니다.`,
                  offset: index * 70,
                });
              }
            });
          }
        }).catch(() => {});
      }).catch(() => {});
    },
    /**
     * 날짜 포맷터
     * @author jeee12
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    /**
     * 테이블 헤더 검색 조건 change
     * @author jeee12
     */
    changeTableCondition() {
      this.loadData();
    },
    formatSchedulePeriod(row, column, value) {
      if (value === 'N') {
        return '한번';
      } else if (value === 'D') {
        return '매일';
      } else if (value === 'W') {
        return '매주';
      } else if (value === 'M') {
        return '월간';
      }
      return EMPTY_TEXT;
    },
    formatModelingResult(row) {
      const virtualcenterList = row.virtualcenterList;
      let apiReplyFlag = '성공';
      virtualcenterList.forEach((vc) => {
        if (isNil(vc.apiReply)) {
          apiReplyFlag = EMPTY_TEXT;
        } else if (!isTrue(vc.apiReply)) {
          apiReplyFlag = '실패';
        }
      });
      return apiReplyFlag;
    },
    formatVirtualcenterUrl(row) {
      const virtualcenterList = row.virtualcenterList;
      let tableValue = virtualcenterList[0].virtualcenterUrl;
      if (virtualcenterList.length > 1) {
        tableValue += ` 외 ${virtualcenterList.length - 1}개`;
      }
      return tableValue;
    },
  },
};
</script>

<style module>
</style>



// WEBPACK FOOTER //
// src/views/settings/basic/DeviceRegistration/Virtual/Modeling.vue